import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { getCreditUsageHistory } from "../../utils/billing";
import { getWorkspaceDetails } from "../../utils/workspaces";
import { formatDateToHumanReadable } from "../../utils/utils";
import {
  fetchAllUserNames,
  fetchWorkspaceUserNames,
  getDisplayName,
  type UserNameMap,
} from "../../utils/userNames";
import CollapsibleSection from "../Billing/CollapsibleSection";
import SectionHeading from "./SectionHeading";
import GatewayUsage from "./GatewayUsage";
import R2Usage from "./R2Usage";

const PAGE_SIZE = 100;
const USAGE_TYPES = "deduct,return";

type RangePreset = "all" | "24h" | "7d" | "30d" | "custom";

interface UsageRange {
  preset: RangePreset;
  // Local calendar dates (YYYY-MM-DD) chosen for the custom range.
  from?: string;
  to?: string;
  // Resolved once when the range is picked, so every page and the totals
  // share the same window instead of drifting with the clock.
  startDate?: string;
  endDate?: string;
}

interface UsageTotals {
  deducted: number;
  returned: number;
  count: number;
}

interface UsageRow {
  id: string;
  type: string;
  triggeredByUserId: string;
  dateTime: string;
  action: string;
  credits: number | string;
  studio: string;
}

// Shape of a record returned by the billing service's usage history.
interface UsageRecord {
  _id: string;
  type?: string;
  triggeredByUserId: string;
  createdAt: string;
  amount: number | string;
  meta?: { info?: string };
  service?: { serviceGroupName?: string } | null;
}

interface UsagePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const PRESET_MS: Record<string, number> = {
  "24h": 24 * 60 * 60_000,
  "7d": 7 * 24 * 60 * 60_000,
  "30d": 30 * 24 * 60 * 60_000,
};

const PRESETS: { value: RangePreset; label: string }[] = [
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "all", label: "All" },
  { value: "custom", label: "Custom" },
];

const resolveUsageRange = (
  preset: RangePreset,
  from?: string,
  to?: string,
): UsageRange => {
  if (preset === "custom") {
    return {
      preset,
      from,
      to,
      startDate: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
      endDate: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
    };
  }
  if (preset === "all") return { preset };
  return {
    preset,
    startDate: new Date(Date.now() - PRESET_MS[preset]).toISOString(),
  };
};

const integerFormat = new Intl.NumberFormat();

const formatCredits = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatUsageId = (id: string) => {
  if (!id || id.length <= 12) return id || "-";
  return `${id.slice(0, 8)}...${id.slice(-6)}`;
};

const headClass =
  "bg-[#faf9fb] h-11 px-5 py-4 border-b border-[#eae7ec] font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px]";
const cellClass =
  "font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[14px] leading-[18px]";
const pagerButtonClass =
  "h-9 rounded-lg border border-[#d7d3dc] bg-white px-3 font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] disabled:cursor-not-allowed disabled:border-[#eae7ec] disabled:bg-[#f5f3f6] disabled:text-[#a7a3ad]";

function RangeFilter({
  range,
  onChange,
}: {
  range: UsageRange;
  onChange: (range: UsageRange) => void;
}) {
  const today = new Date().toLocaleDateString("en-CA");
  const dateInputClass =
    "h-9 rounded-lg border border-[#d7d3dc] bg-white px-2 text-[14px] text-[#211f26]";
  return (
    <div className="flex flex-wrap items-center gap-3 w-full">
      <div
        className="inline-flex items-center gap-1 rounded-xl border border-[#eae7ec] bg-white p-1"
        role="group"
        aria-label="Usage date range"
      >
        {PRESETS.map(({ value, label }) => {
          const active = range.preset === value;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={active}
              onClick={() =>
                onChange(
                  value === "custom"
                    ? resolveUsageRange("custom", range.from, range.to)
                    : resolveUsageRange(value),
                )
              }
              className={`h-8 rounded-lg px-4 font-['Geist:Medium',_sans-serif] font-medium text-[14px] leading-[18px] text-[#211f26] transition-colors ${
                active ? "bg-[#e6f0ff]" : "hover:bg-[#f5f3f6]"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      {range.preset === "custom" && (
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-[#5f5a66]">
          <label className="flex items-center gap-2">
            From
            <input
              type="date"
              aria-label="From date"
              value={range.from || ""}
              max={range.to || today}
              onChange={(e) =>
                onChange(
                  resolveUsageRange(
                    "custom",
                    e.target.value || undefined,
                    range.to,
                  ),
                )
              }
              className={dateInputClass}
            />
          </label>
          <label className="flex items-center gap-2">
            To
            <input
              type="date"
              aria-label="To date"
              value={range.to || ""}
              min={range.from}
              max={today}
              onChange={(e) =>
                onChange(
                  resolveUsageRange(
                    "custom",
                    range.from,
                    e.target.value || undefined,
                  ),
                )
              }
              className={dateInputClass}
            />
          </label>
        </div>
      )}
    </div>
  );
}

function UsageStats({
  totals,
  isLoading,
}: {
  totals: UsageTotals | null;
  isLoading: boolean;
}) {
  const stats = [
    { label: "Credits used", value: totals?.deducted },
    { label: "Credits returned", value: totals?.returned },
    {
      label: "Net credits used",
      value: totals ? totals.deducted - totals.returned : undefined,
    },
    { label: "Transactions", value: totals?.count },
  ];
  return (
    <div
      className="grid w-full grid-cols-2 gap-3 md:grid-cols-4"
      data-name="Usage totals"
    >
      {stats.map(({ label, value }) => (
        <div
          key={label}
          className="rounded-2xl border border-[#eae7ec] bg-[rgba(253,252,253,0.88)] px-4 py-3"
        >
          <div className="font-['Geist:Medium',_sans-serif] font-medium text-[#84828e] text-[12px] leading-[16px] uppercase">
            {label}
          </div>
          <div className="mt-1 font-['Geist:SemiBold',_sans-serif] font-semibold text-[#211f26] text-[22px] leading-[28px]">
            {isLoading ? "…" : value === undefined ? "—" : formatCredits(value)}
          </div>
        </div>
      ))}
    </div>
  );
}

function UsageTable({
  rows,
  pagination,
  isLoading,
  isFiltered,
  onPageChange,
  userNames,
}: {
  rows: UsageRow[];
  pagination: UsagePagination;
  isLoading: boolean;
  isFiltered: boolean;
  onPageChange: (page: number) => void;
  userNames: UserNameMap;
}) {
  if (rows.length === 0) {
    return (
      <div className="w-full rounded-2xl border border-[#eae7ec] px-6 py-10 text-center text-[14px] text-[#84828e]">
        {isLoading
          ? "Loading credit usage..."
          : isFiltered
            ? "No credit usage was recorded in the selected date range."
            : "No credit usage recorded for this workspace yet."}
      </div>
    );
  }

  const from = (pagination.page - 1) * pagination.limit + 1;
  const to = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div
      className="backdrop-blur-[20px] backdrop-filter bg-[rgba(253,252,253,0.88)] border border-[#eae7ec] relative rounded-2xl w-full overflow-hidden"
      data-name="Usage table"
    >
      {/* The one scroll area for this table, capped so 100 rows do not stretch
          the page. It scrolls both ways but hides its scrollbar tracks
          (scrollbar-width for Firefox and current Chrome, a zero-size
          ::-webkit-scrollbar for Safari). The shared Table wraps itself in its
          own overflow container, which would add a second scrollbar and
          unpin the sticky header, so that wrapper is made non-scrolling here. */}
      <div className="max-h-[560px] w-full overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:h-0 [&::-webkit-scrollbar]:w-0 [&>[data-slot=table-container]]:overflow-visible">
        {/* Columns with predictable content get fixed widths; User takes a
              share of the rest and Action the remainder, so neither the IDs
              are clipped nor one column soaks up all the spare width. */}
        <Table className="w-full min-w-[1180px] table-fixed">
          <TableHeader className="sticky top-0 z-10">
            <TableRow className="border-b-0">
              <TableHead className={`${headClass} w-[190px]`}>ID</TableHead>
              <TableHead className={`${headClass} w-[20%]`}>USER</TableHead>
              <TableHead className={`${headClass} w-[210px]`}>
                DATE &amp; TIME
              </TableHead>
              <TableHead className={headClass}>ACTION</TableHead>
              <TableHead className={`${headClass} w-[110px]`}>TYPE</TableHead>
              <TableHead className={`${headClass} w-[100px] text-right`}>
                CREDITS
              </TableHead>
              <TableHead className={`${headClass} w-[150px]`}>APP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((usage, index) => (
              <TableRow
                key={usage.id || index}
                className="h-[52px] border-b border-[#faf9fb] hover:bg-[#faf9fb]/70"
              >
                <TableCell className="px-5 py-2">
                  <div className={`${cellClass} truncate`} title={usage.id}>
                    {formatUsageId(usage.id)}
                  </div>
                </TableCell>
                <TableCell className="px-5 py-2">
                  <div className="min-w-0" title={usage.triggeredByUserId}>
                    <div className={`${cellClass} truncate`}>
                      {getDisplayName(usage.triggeredByUserId, userNames)}
                    </div>
                    {/* The ID beneath a resolved name, for cross-checking. */}
                    {usage.triggeredByUserId &&
                      userNames[usage.triggeredByUserId] && (
                        <div className="truncate text-[12px] leading-[16px] text-[#84828e]">
                          {usage.triggeredByUserId}
                        </div>
                      )}
                  </div>
                </TableCell>
                <TableCell className="px-5 py-2">
                  <div className={cellClass}>{usage.dateTime}</div>
                </TableCell>
                <TableCell className="px-5 py-2">
                  <div className={`${cellClass} truncate`} title={usage.action}>
                    {usage.action}
                  </div>
                </TableCell>
                <TableCell className="px-5 py-2">
                  <div className="inline-flex h-7 items-center rounded-full bg-[#f2eff3] px-3 font-['Geist:Medium',_sans-serif] font-medium text-[#211f26] text-[13px] leading-[18px]">
                    {usage.type}
                  </div>
                </TableCell>
                <TableCell className="px-5 py-2 text-right">
                  <div
                    className={`${cellClass} ${
                      usage.type === "return"
                        ? "text-[#039855]"
                        : "text-[#d92d20]"
                    }`}
                  >
                    {usage.type === "return" ? "+" : "-"}
                    {usage.credits}
                  </div>
                </TableCell>
                <TableCell className="px-5 py-2">
                  <div className={`${cellClass} truncate`}>{usage.studio}</div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-between gap-3 border-t border-[#eae7ec] bg-[#faf9fb] px-5 py-3 text-[13px] text-[#5f5a66]">
        <div className="leading-[18px]">
          <span className="font-medium text-[#211f26]">
            {from}-{to}
          </span>{" "}
          of {pagination.total} usage records
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            className={pagerButtonClass}
            disabled={!pagination.hasPreviousPage || isLoading}
            onClick={() => onPageChange(Math.max(1, pagination.page - 1))}
          >
            Previous
          </button>
          <span className="min-w-[88px] text-center leading-[18px]">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className={pagerButtonClass}
            disabled={!pagination.hasNextPage || isLoading}
            onClick={() => onPageChange(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

// Owns paging for the credit records table. It mounts only while the table
// is expanded, so the paged request is made only for someone looking at it;
// the page keys it on the date window, which restarts it at page 1.
function CreditUsageRecords({
  workspaceId,
  range,
  userNames,
}: {
  workspaceId: string;
  range: UsageRange;
  userNames: UserNameMap;
}) {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<UsageRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<UsagePagination>({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  // Guards against a slow response for an old page overwriting a newer one.
  const requestRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestRef.current;
    setLoading(true);
    getCreditUsageHistory(workspaceId, {
      types: USAGE_TYPES,
      startDate: range.startDate,
      endDate: range.endDate,
      limit: PAGE_SIZE,
      page,
    })
      .then((response) => {
        if (requestId !== requestRef.current) return;
        const data: UsageRow[] = (response.data || []).map(
          (item: UsageRecord) => ({
            id: item._id,
            type: item.type || "-",
            triggeredByUserId: item.triggeredByUserId,
            dateTime: formatDateToHumanReadable(item.createdAt, true),
            action: item.meta?.info || "-",
            credits: item.amount,
            studio: item.service?.serviceGroupName || "-",
          }),
        );
        setRows(data);
        setPagination(
          response.pagination || {
            page,
            limit: PAGE_SIZE,
            total: data.length,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: page > 1,
          },
        );
      })
      .catch((error) => console.error("Error fetching credit usage:", error))
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
  }, [workspaceId, range, page]);

  return (
    <UsageTable
      rows={rows}
      pagination={pagination}
      isLoading={loading}
      isFiltered={Boolean(range.startDate || range.endDate)}
      onPageChange={setPage}
      userNames={userNames}
    />
  );
}

export default function TrackUsage() {
  const params = useParams();
  const workspaceId = params.workspaceId;

  const [workspaceName, setWorkspaceName] = useState("");
  const [userNames, setUserNames] = useState<UserNameMap>({});
  const [range, setRange] = useState<UsageRange>(() =>
    resolveUsageRange("24h"),
  );
  const [totals, setTotals] = useState<UsageTotals | null>(null);
  const [totalsLoading, setTotalsLoading] = useState(false);
  // Guards against a slow response for an old range overwriting a newer one.
  const totalsRequestRef = useRef(0);

  useEffect(() => {
    if (!workspaceId) return;
    getWorkspaceDetails(workspaceId)
      .then((res) => setWorkspaceName(res?.data?.name || ""))
      .catch((err) => console.error("Error fetching workspace details:", err));
    fetchWorkspaceUserNames(workspaceId).then((map) =>
      setUserNames((current) => ({ ...current, ...map })),
    );
    fetchAllUserNames().then((map) =>
      setUserNames((current) => ({ ...map, ...current })),
    );
  }, [workspaceId]);

  // The usage API has no aggregate, so totals come from every record in the
  // range (no limit), independent of which page the table is showing.
  useEffect(() => {
    if (!workspaceId) return;
    const requestId = ++totalsRequestRef.current;
    setTotalsLoading(true);
    getCreditUsageHistory(workspaceId, {
      types: USAGE_TYPES,
      startDate: range.startDate,
      endDate: range.endDate,
    })
      .then((response) => {
        if (requestId !== totalsRequestRef.current) return;
        setTotals(
          (response.data || []).reduce(
            (
              acc: UsageTotals,
              item: { type?: string; amount?: number | string },
            ) => {
              const amount = Number(item.amount) || 0;
              if (item.type === "return") acc.returned += amount;
              else acc.deducted += amount;
              acc.count += 1;
              return acc;
            },
            { deducted: 0, returned: 0, count: 0 },
          ),
        );
      })
      .catch((error) => {
        console.error("Error fetching credit usage totals:", error);
        if (requestId === totalsRequestRef.current) setTotals(null);
      })
      .finally(() => {
        if (requestId === totalsRequestRef.current) setTotalsLoading(false);
      });
  }, [workspaceId, range]);

  return (
    <div
      className="box-border flex flex-col gap-6 items-start justify-start relative size-full min-h-full pb-6"
      data-name="Track Usage"
    >
      <div className="flex w-full flex-col gap-3">
        <p className="font-['Geist:SemiBold',_sans-serif] font-semibold text-[#211f26] text-[18px] leading-[30px]">
          Usage{workspaceName ? ` - ${workspaceName}` : ""}
        </p>
        <RangeFilter range={range} onChange={setRange} />
      </div>

      <section className="flex w-full flex-col gap-3" data-name="Credit usage">
        <SectionHeading title="Credit usage" />
        <UsageStats totals={totals} isLoading={totalsLoading} />
        {workspaceId && (
          <div className="w-full">
            <CollapsibleSection
              title="Usage records"
              subtitle={
                totals
                  ? `${integerFormat.format(totals.count)} in range`
                  : undefined
              }
            >
              <CreditUsageRecords
                key={`${range.startDate}|${range.endDate}`}
                workspaceId={workspaceId}
                range={range}
                userNames={userNames}
              />
            </CollapsibleSection>
          </div>
        )}
      </section>

      {workspaceId && (
        <>
          <GatewayUsage
            workspaceId={workspaceId}
            startDate={range.startDate}
            endDate={range.endDate}
            userNames={userNames}
          />
          <R2Usage
            workspaceId={workspaceId}
            startDate={range.startDate}
            endDate={range.endDate}
          />
        </>
      )}
    </div>
  );
}
